import React, { useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeAltIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const ENGINE_STYLES = {
  ChatGPT: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  Ahrefs: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  SEMrush: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  Moz: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  Copilot: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  Perplexity: { bg: 'bg-sky-50', text: 'text-sky-700', dot: 'bg-sky-500' },
  'Google AI': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
};

const RANK_BADGE_COLORS = {
  1: 'bg-green-100 text-green-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-purple-100 text-purple-700',
  default: 'bg-gray-100 text-gray-600',
};

const AICitationHistory = ({ data = [], loading = false }) => {
  const [currentIdx, setCurrentIdx] = useState(0);

  const currentItem = data[currentIdx] || null;
  const totalItems = data.length;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 h-full" role="status" aria-label="Loading citation history">
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between">
            <div className="h-6 bg-gray-200 rounded w-40"></div>
            <div className="flex gap-1">
              <div className="h-8 w-8 bg-gray-200 rounded"></div>
              <div className="h-8 w-8 bg-gray-200 rounded"></div>
              <div className="h-8 w-8 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!currentItem) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 h-full flex flex-col">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Citation History</h3>
        <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
          <DocumentTextIcon className="h-10 w-10 text-gray-300 mb-3" aria-hidden="true" />
          <p className="text-sm font-medium text-gray-500">Citation history will appear here.</p>
          <p className="text-xs text-gray-400 mt-1">View historical citations across AI engines.</p>
        </div>
        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
          <button type="button" className="flex-1 bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" aria-label="View all citations">
            View All Citations
          </button>
          <button type="button" className="flex-1 bg-white text-gray-700 text-sm font-medium py-2 px-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" aria-label="View citation history timeline">
            View History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 h-full flex flex-col">
      {/* Header with pagination */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">AI Citation History</h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
            disabled={currentIdx === 0}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous citation"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          {data.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCurrentIdx(i)}
              className={`min-w-[28px] h-7 rounded-lg text-xs font-medium transition-colors ${
                currentIdx === i
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
              aria-label={`Citation ${i + 1}`}
              aria-current={currentIdx === i ? 'true' : undefined}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setCurrentIdx((i) => Math.min(totalItems - 1, i + 1))}
            disabled={currentIdx === totalItems - 1}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Next citation"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Prompt info */}
      <div className="mb-4">
        <p className="text-sm text-gray-500 mb-1.5">
          <span className="font-medium text-gray-700">Prompt:</span>{' '}
          <span className="text-gray-600">{currentItem.prompt}</span>
        </p>
        {currentItem.rankBadge && (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            RANK_BADGE_COLORS[currentItem.rankBadge.rank] || RANK_BADGE_COLORS.default
          }`}>
            Rank {currentItem.rankBadge.rank} {currentItem.rankBadge.label || ''}
          </span>
        )}
      </div>

      {/* Citation sources - left panel (engines with ranks) */}
      <div className="grid grid-cols-1 gap-3 flex-1 min-h-0">
        {/* Cited sources list */}
        <div className="space-y-2.5">
          {currentItem.citations.map((citation, idx) => {
            const style = ENGINE_STYLES[citation.source] || ENGINE_STYLES.ChatGPT;
            return (
              <div
                key={idx}
                className={`flex items-center gap-3 p-2.5 rounded-lg ${style.bg} transition-colors`}
                role="listitem"
              >
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${style.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${style.text}`}>
                      {citation.source}
                    </span>
                    {citation.rank && (
                      <span className="text-xs text-gray-500 bg-white/70 px-1.5 py-0.5 rounded font-medium">
                        {citation.rank}
                      </span>
                    )}
                  </div>
                  {citation.domain && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <GlobeAltIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span className="text-xs text-gray-500 truncate">{citation.domain}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
        <button
          type="button"
          className="flex-1 bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          aria-label="View all citations"
        >
          View All Citations
        </button>
        <button
          type="button"
          className="flex-1 bg-white text-gray-700 text-sm font-medium py-2 px-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          aria-label="View citation history timeline"
        >
          View History
        </button>
      </div>
    </div>
  );
};

export default AICitationHistory;
